import classNames from 'classnames';
import React, { useCallback, useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { useFormService } from '@bingads-webui-react/form';
import { DisplayPlusNativeAds } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { withScope } from '@bingads-webui-react/scope-service';

import { NativeAdFooter } from './native-ad-footer';
import { getDefaultCampaign } from '../constants';
import { useBiddingScheme } from '../../../common/bidding-schemes';
import { getStyles } from '../../../shared/image-experience/style';
import { StepHeader } from '../../../shared/step-header';

const NativeAdInternal = ({
  data: ads,
  error,
  onDataChange,
  scope,
  hideFooter,
  supportImageFitting,
  loadClipchampPlayer = null,
}) => {
  const {
    errors = [],
  } = error || {};
  const { data } = ads;

  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);

  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const finalHideFooter = hideFooter || !permissions.dynamic.DisplayPlusMultipleAds;

  const { state } = useWizardService();
  const { editingAdId } = state.context.wizardData;

  const { state: { context: { saveFailed } } } = useFormService();

  const { cid, aid } = scope.values || {};
  const biddingScheme = useBiddingScheme({
    cid, aid, getDefaultCampaign, i18n,
  });

  const currentStates = state.toStrings()[1];

  const { IsAmplifyEnabled } = Permissions;
  const { DisplayPlusAmplify } = DynamicPermissions;

  const stylingProps = useMemo(() =>
    ({ component: { placements: (IsAmplifyEnabled && DisplayPlusAmplify) ? 11 : 7 } }), [DisplayPlusAmplify, IsAmplifyEnabled]);

  const getClasses = useResponsiveStyles(getStyles, stylingProps);

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onNativeAdsStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onNativeAdsStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  const onValidate = useCallback((result) => {
    onDataChange({
      data: {
        data: {
          ...data,
          ...result.data,
        },
      },
      isValid: result.isValid,
    });
  }, [data, onDataChange]);

  return (
    <div className={classNames(getClasses('nativeAdStepShell'), 'native-ad-step')}>
      <ActuationGroup actuationProps={{ isActive: saveFailed }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors: saveFailed }}>
            <ValidationGroup
              validationProps={{
                schema: AssetGroup,
                onValidate,
                data,
                i18n,
              }}
            >
              <div className={classNames(getClasses('headerStackStyles'))}>
                <StepHeader
                  title={editingAdId ? _TL_('Edit your audience ad') : _TL_('Create your audience ad')}
                  description={_TL_('Provide the details and assets you want to include in your ad. We\'ll mix and match the most optimal combinations to create an effective, high-performing ad. Note: You can only create audience ads in this campaign.')}
                />
                <div className="server-errors">
                  <UnmappedFormErrors />
                </div>
              </div>
              <DisplayPlusNativeAds
                data={ads}
                editingAdId={editingAdId}
                onDataChange={onDataChange}
                isSaving={matchStepState(state, STEP_STATES.SAVING)}
                isCanceling={matchStepState(state, STEP_STATES.CANCELING)}
                biddingScheme={biddingScheme}
                supportImageFitting={supportImageFitting}
                loadClipchampPlayer={loadClipchampPlayer}
              />
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
      {!finalHideFooter && <NativeAdFooter />}
    </div>
  );
};

NativeAdInternal.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({
      FinalUrls: PropTypes.string,
      MobileFinalUrls: PropTypes.string,
      BusinessName: PropTypes.string,
      Headlines: PropTypes.arrayOf(PropTypes.string),
      LongHeadlines: PropTypes.arrayOf(PropTypes.string),
      Descriptions: PropTypes.arrayOf(PropTypes.string),
      Images: PropTypes.shape({}),
      TrackingUrlTemplate: PropTypes.string,
      FinalUrlSuffix: PropTypes.string,
      UrlCustomParameters: PropTypes.string,
      ImpressionTrackingUrls: PropTypes.string,
      VerifiedTrackingSettings: PropTypes.shape({}),
      FinalUrlForRecommendation: PropTypes.string,
      CallToAction: PropTypes.shape({
        cta: PropTypes.string,
        language: PropTypes.string,
      }),
      hotspots: PropTypes.shape({}),
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }).isRequired,
  hideFooter: PropTypes.bool,
  supportImageFitting: PropTypes.bool,
};

NativeAdInternal.defaultProps = {
  hideFooter: false,
  supportImageFitting: false,
};

export const NativeAd = withScope(NativeAdInternal);
